import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  Button,
  DataTable,
  FullPageStatus,
  Input,
  Modal,
  showToast,
  type DataTableColumn,
} from "@vv/ui";
import { teamDetailPath } from "@vv/types";
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import type { Team } from "../domain/team.types";
import { useTeamsDispatch, useTeamsSelector } from "../state/hooks";
import { createTeam, fetchTeams } from "../state/teams.thunks";

export interface TeamsListPageProps {
  isOwner: boolean;
}

interface TeamFormState {
  name: string;
  description: string;
}

const emptyForm: TeamFormState = { name: "", description: "" };

export function TeamsListPage({ isOwner }: TeamsListPageProps) {
  const dispatch = useTeamsDispatch();
  const navigate = useNavigate();
  const teams = useTeamsSelector((state) => state.teams.teams);
  const status = useTeamsSelector((state) => state.teams.status);
  const error = useTeamsSelector((state) => state.teams.error);

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<TeamFormState>(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(fetchTeams());
  }, [dispatch]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      await dispatch(
        createTeam({ name: form.name.trim(), description: form.description.trim() || undefined }),
      ).unwrap();
      showToast("Team created", "success");
      setFormOpen(false);
      setForm(emptyForm);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Could not create team", "error");
    } finally {
      setSaving(false);
    }
  }

  const columns: DataTableColumn<Team>[] = [
    { key: "name", header: "Name", render: (team) => team.name, sortable: true },
    { key: "description", header: "Description", render: (team) => team.description || "—" },
    {
      key: "isActive",
      header: "Status",
      render: (team) => (team.isActive ? "Active" : "Inactive"),
    },
  ];

  if (status === "loading" && teams.length === 0) {
    return <FullPageStatus variant="loading" title="Loading teams..." />;
  }

  if (status === "failed" && teams.length === 0) {
    return (
      <FullPageStatus
        variant="error"
        title="Could not load teams"
        description={error ?? undefined}
        action={
          <Button variant="contained" onClick={() => dispatch(fetchTeams())}>
            Retry
          </Button>
        }
      />
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Validation Teams
        </Typography>
        {isOwner ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setFormOpen(true)}>
            New team
          </Button>
        ) : null}
      </Stack>

      {status === "succeeded" && teams.length === 0 ? (
        <FullPageStatus
          variant="empty"
          title="No teams yet"
          description={
            isOwner
              ? "Create your first validation team to get started."
              : "No validation teams have been created yet."
          }
          action={
            isOwner ? (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setFormOpen(true)}>
                New team
              </Button>
            ) : undefined
          }
        />
      ) : (
        <DataTable<Team>
          columns={columns}
          rows={teams}
          getRowId={(team) => team.id}
          loading={status === "loading"}
          onRowClick={(team) => navigate(teamDetailPath(team.id))}
        />
      )}

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title="New team"
        actions={
          <>
            <Button onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button
              type="submit"
              form="team-form"
              variant="contained"
              loading={saving}
              disabled={form.name.trim().length === 0}
            >
              Save
            </Button>
          </>
        }
      >
        <Stack component="form" id="team-form" spacing={2} onSubmit={handleSubmit} sx={{ pt: 1 }}>
          <Input
            label="Name"
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            required
            autoFocus
          />
          <Input
            label="Description"
            value={form.description}
            onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
            multiline
            minRows={3}
          />
        </Stack>
      </Modal>
    </Box>
  );
}
